import DOMPurify from 'isomorphic-dompurify'
import { marked } from 'marked'
import { clauseCards } from './transforms/clauseCards'
import { emojiIcons } from './transforms/emojiIcons'
import { collapsibleSections } from './transforms/collapsible'
import { legalReference } from './transforms/legalReference'
import { matchLegalSectionCitationAtStart, repairLegalCitationMarkers } from '#shared/legalCitations'

/** A pure function that transforms an HTML (or raw content) string. */
export type HtmlTransform = (html: string) => string

/** Apply an ordered list of transforms via reduce. */
export function applyTransforms(html: string, transforms: HtmlTransform[]): string {
  return transforms.reduce((acc, fn) => fn(acc), html)
}

// ── Marked citation extension ──────────────────────────────────────
// Citations arrive as markdown links with cite: protocol:
//   [@Art. 130 — Código de Trabajo](cite:section/<section_id>)
// Since marked doesn't recognize cite: as a URL scheme, we register a
// custom inline tokenizer that matches before the default link parser.
marked.use({
  extensions: [{
    name: 'citation',
    level: 'inline',
    start(src: string) {
      const idx = src.indexOf('](cite:')
      if (idx === -1) return -1
      const open = src.lastIndexOf('[', idx)
      return open >= 0 ? open : -1
    },
    tokenizer(src: string) {
      const match = matchLegalSectionCitationAtStart(src)
      if (match) {
        return {
          type: 'citation',
          raw: match.markdown,
          label: match.label,
          sectionId: match.sectionId,
        }
      }
    },
    renderer(token: any) {
      const safeLabel = (token.label as string).replace(/"/g, '&quot;')
      return `<span class="marcus-cite" data-section-id="${token.sectionId}" data-label="${safeLabel}" tabindex="0" role="button" aria-label="${safeLabel}">${token.label}</span>`
    },
  }],
})

// Pre-markdown: run on raw content before marked.parse()
// `repairLegalCitationMarkers` runs FIRST: the citation tokenizer below needs a closed href, and a
// marker the model comma-joined without closing would otherwise reach the reader as raw markdown.
const preTransforms: HtmlTransform[] = [repairLegalCitationMarkers, clauseCards]

// Post-markdown: run on HTML output after marked.parse()
const postTransforms: HtmlTransform[] = [legalReference, collapsibleSections, emojiIcons]

// ── DOMPurify config ────────────────────────────────────────────────
const PURIFY_CONFIG = {
  ADD_TAGS: ['details', 'summary', 'svg', 'path', 'circle', 'rect', 'line'],
  ADD_ATTR: [
    'class', 'open', 'role', 'aria-label', 'tabindex',
    'data-section-id', 'data-label',
    'viewBox', 'fill', 'stroke', 'stroke-width', 'stroke-linecap',
    'stroke-linejoin', 'd', 'cx', 'cy', 'r', 'x', 'y', 'width',
    'height', 'rx', 'ry', 'xmlns',
  ],
}

/**
 * Format a chat message: raw content → styled, sanitized HTML.
 *
 * Pipeline:
 *   raw content → pre-transforms → marked.parse() → post-transforms → DOMPurify
 */
export function formatMessage(content: string): string {
  const prepped = applyTransforms(content, preTransforms)
  const html = marked.parse(prepped, { breaks: true }) as string
  const styled = applyTransforms(html, postTransforms)
  return DOMPurify.sanitize(styled, PURIFY_CONFIG) as string
}
